'use client'

import FileDocumentIcon from '@/assets/icons/file_document.svg'
import FolderIcon from '@/assets/icons/folder.svg'
import { Badge } from '@/components/ui/badge'
import { MultiSelect, type SelectOption } from '@/components/ui/select'
import { cn, getSelectionKey } from '@/lib/utils'
import type { DataFile } from '@/types'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ALL_DEPARTMENTS_VALUE, getDepartmentIds, getDepartmentSelection, getDepartmentValues } from './helpers'

type DataNameCellProps = {
  item: DataFile
  onNavigateFolder: (folder: DataFile) => void
}

type DataDepartmentCellProps = {
  item: DataFile
  departmentOptions: SelectOption[]
  onDepartmentsSave: (item: DataFile, departmentIds: number[]) => void
}

type DataStatusCellProps = {
  status?: DataFile['status'] | null
}

function getStatusColor(status: NonNullable<DataFile['status']>) {
  if (status === 'pending' || status === 'processing') return 'yellow'
  if (status === 'success') return 'green'
  return 'red'
}

export function DataNameCell({ item, onNavigateFolder }: DataNameCellProps) {
  const isFolder = item.type === 'folder'

  return (
    <button
      type="button"
      disabled={!isFolder}
      onClick={() => isFolder && onNavigateFolder(item)}
      className={cn(
        'flex items-center gap-2 text-left text-secondary-600',
        isFolder ? 'cursor-pointer hover:underline' : 'cursor-default',
      )}
    >
      {isFolder ? (
        <FolderIcon width={16} height={16} className="text-secondary-400" />
      ) : (
        <FileDocumentIcon width={16} height={16} className="text-secondary-400" />
      )}
      <span className="truncate max-w-56">{item.name}</span>
    </button>
  )
}

export function DataDepartmentCell({ item, departmentOptions, onDepartmentsSave }: DataDepartmentCellProps) {
  const t = useTranslations('admin.config.data')
  const initialSelected = useMemo(() => getDepartmentValues(item.departments), [item.departments])
  const [selected, setSelected] = useState<string[]>(initialSelected)
  const savedKeyRef = useRef(getSelectionKey(initialSelected))

  useEffect(() => {
    setSelected(initialSelected)
    savedKeyRef.current = getSelectionKey(initialSelected)
  }, [initialSelected])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) return
      const selectionKey = getSelectionKey(selected)
      if (selectionKey === savedKeyRef.current) return
      onDepartmentsSave(item, getDepartmentIds(selected))
      setSelected(initialSelected)
    },
    [initialSelected, item, onDepartmentsSave, selected],
  )

  return (
    <MultiSelect
      options={[{ value: ALL_DEPARTMENTS_VALUE, label: t('allDepartmentsAccess') }, ...departmentOptions]}
      value={selected}
      onChange={(next) => setSelected(getDepartmentSelection(selected, next))}
      onOpenChange={handleOpenChange}
      placeholder={t('allDepartment')}
      size="md"
      floatingLabel={false}
    />
  )
}

export function DataStatusCell({ status }: DataStatusCellProps) {
  const t = useTranslations('admin.config.data.status')
  if (!status) return null

  return (
    <Badge
      label={t(status)}
      color={getStatusColor(status)}
      className="rounded-round"
      leadingIcon={
        <div className="size-3 p-0.5">
          <div className="size-2 rounded-full" style={{ backgroundColor: 'currentColor' }} />
        </div>
      }
    />
  )
}
